import React, { useState } from "react";
import Icon from "../icons/Icon";
import Modal from "../ui/Modal";
import MultiSelect from "../ui/MultiSelect";
import ProjectTypeFilter from "./ProjectTypeFilter";
import ProjectStatusFilter from "./ProjectStatusFilter";
import ProjectSituationFilter from "./ProjectSituationFilter";
import { useProjectCoordinators } from "../../hooks/useProjectCoordinators";
import { ALL_TYPES, DEFAULT_STATUS } from "../../domain/projects";

const LABEL = "Filtrar projetos";

function triggerLabel(activeCount) {
  if (activeCount === 0) return LABEL;
  return `${LABEL} (${activeCount} ${activeCount === 1 ? "filtro ativo" : "filtros ativos"})`;
}

function activeFilterCount(type, status, situations, coordinators) {
  return (
    (type !== ALL_TYPES ? 1 : 0) +
    (status !== DEFAULT_STATUS ? 1 : 0) +
    situations.length +
    coordinators.length
  );
}

function ProjectFilters({ type, status, situations, coordinators, onApply }) {
  const [open, setOpen] = useState(false);
  const [everOpened, setEverOpened] = useState(false);
  const [draft, setDraft] = useState({ type, status, situations, coordinators });

  const { options, loading, error, retry } = useProjectCoordinators(everOpened);

  const openModal = () => {
    setDraft({ type, status, situations, coordinators });
    setEverOpened(true);
    setOpen(true);
  };

  const apply = () => {
    setOpen(false);
    onApply(draft);
  };

  const clear = () =>
    setDraft({
      type: ALL_TYPES,
      status: DEFAULT_STATUS,
      situations: [],
      coordinators: [],
    });

  const appliedCount = activeFilterCount(type, status, situations, coordinators);
  const toggleClass = appliedCount > 0 ? "filter-toggle is-filtering" : "filter-toggle";

  return (
    <>
      <button
        className={`btn btn-icon ${toggleClass}`}
        type="button"
        aria-label={triggerLabel(appliedCount)}
        aria-haspopup="dialog"
        onClick={openModal}
      >
        <Icon name="filter" />
        {appliedCount > 0 && (
          <span className="btn-badge" aria-hidden="true">
            {appliedCount}
          </span>
        )}
      </button>

      {open && (
        <Modal
          title={LABEL}
          onClose={() => setOpen(false)}
          closeButton
          footer={
            <>
              <button className="btn btn-secondary" type="button" onClick={clear}>
                Limpar
              </button>
              <button className="btn btn-primary" type="button" onClick={apply}>
                Aplicar
              </button>
            </>
          }
        >
          <div className="filter-field">
            <span className="filter-field-label" aria-hidden="true">
              Tipo
            </span>
            <ProjectTypeFilter
              selectedType={draft.type}
              onTypeChange={(nextType) => setDraft((current) => ({ ...current, type: nextType }))}
            />
          </div>

          <div className="filter-field">
            <span className="filter-field-label" aria-hidden="true">
              Status
            </span>
            <ProjectStatusFilter
              selectedStatus={draft.status}
              onStatusChange={(nextStatus) => setDraft((current) => ({ ...current, status: nextStatus }))}
            />
          </div>

          <div className="filter-field">
            <span className="filter-field-label" aria-hidden="true">
              Situação
            </span>
            <ProjectSituationFilter
              selectedSituations={draft.situations}
              onSituationsChange={(nextSituations) =>
                setDraft((current) => ({ ...current, situations: nextSituations }))
              }
            />
          </div>

          <div className="filter-field">
            <span className="filter-field-label" aria-hidden="true">
              Coordenadores
            </span>

            {loading && (
              <p className="filter-field-note" role="status">
                Carregando coordenadores…
              </p>
            )}

            {!loading && error && (
              <div className="filter-field-error">
                <p className="filter-field-note" role="alert">
                  Não foi possível carregar os coordenadores
                </p>
                <button
                  className="filter-field-retry"
                  type="button"
                  aria-label="Tentar novamente"
                  onClick={retry}
                >
                  <Icon name="refresh-cw" />
                </button>
              </div>
            )}

            {!loading && !error && options.length === 0 && (
              <p className="filter-field-note">Nenhum coordenador cadastrado nos projetos.</p>
            )}

            {!loading && !error && options.length > 0 && (
              <MultiSelect
                label="Filtrar por coordenador"
                values={draft.coordinators}
                options={options}
                emptyLabel="Todos os coordenadores"
                onChange={(nextCoordinators) =>
                  setDraft((current) => ({ ...current, coordinators: nextCoordinators }))
                }
              />
            )}
          </div>
        </Modal>
      )}
    </>
  );
}

export default ProjectFilters;
